import imageCompression from 'browser-image-compression';

export interface CompressionResult {
  original: { file: File; size: number };
  compressed: { file: File; size: number };
  thumbnail: { file: File; size: number };
  savings: number; // percentage saved
}

/**
 * Compress an image for upload: generates a gallery-size and thumbnail-size variant.
 * Outputs WebP when browser supports it, JPEG fallback.
 */
export async function compressForUpload(
  file: File,
  onProgress?: (progress: number) => void,
): Promise<CompressionResult> {
  const originalSize = file.size;

  // Compressed gallery image: max 1600px, target ~200KB
  const compressed = await imageCompression(file, {
    maxSizeMB: 0.2,
    maxWidthOrHeight: 1600,
    useWebWorker: true,
    fileType: 'image/webp',
    onProgress: (p) => onProgress?.(p * 0.7), // 0-70% for main compression
  });

  // Thumbnail: max 400px, target ~50KB
  const thumbnail = await imageCompression(file, {
    maxSizeMB: 0.05,
    maxWidthOrHeight: 400,
    useWebWorker: true,
    fileType: 'image/webp',
    onProgress: (p) => onProgress?.(70 + p * 0.3), // 70-100% for thumbnail
  });

  const compressedSize = compressed.size + thumbnail.size;
  const savings = Math.round((1 - compressedSize / originalSize) * 100);

  return {
    original: { file, size: originalSize },
    compressed: {
      file: new File([compressed], renameToWebP(file.name), { type: compressed.type }),
      size: compressed.size,
    },
    thumbnail: {
      file: new File([thumbnail], renameToWebP(file.name, '-thumb'), { type: thumbnail.type }),
      size: thumbnail.size,
    },
    savings: Math.max(0, savings),
  };
}

function renameToWebP(originalName: string, suffix = ''): string {
  const baseName = originalName.replace(/\.[^.]+$/, '');
  return `${baseName}${suffix}.webp`;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
